import React, { useState, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { makeStyles, Box, Card, CardContent } from '@material-ui/core';
import { SwapHorizontalCircle } from '@material-ui/icons';
import {
  AriaLiveRegion,
  ColorButton,
  IconButton,
  IconTooltip,
  OptionsMenu,
} from 'components';

// Use shelfBlue800 constant for focus outline color
const shelfBlue800 = '#002242';

const green = '#05A300';
const gold = '#AD9100';
const red = '#FF2943';

const hex = {
  high: green,
  medium: gold,
  low: red,
};

const StyledBox = styled(Box)(() => ({
  display: 'flex',
}));

const StyledCard = styled(Card)(({ confidencelevel, theme }) => ({
  fontFamily: theme.typography.body2,
  display: 'flex',
  flexDirection: 'column',
  width: theme.spacing(28.75),
  borderLeftColor: hex[confidencelevel] ?? 'transparent',
  borderLeftStyle: 'solid',
  borderWidth: theme.spacing(0, 0, 0, 0.5),
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: theme.spacing(20),
  width: theme.spacing(28.25),
  overflowY: 'auto',
  color: `#394551`,
  textDecoration: 'none',
  maxWidth: '100%',
  maxHeight: '100%',
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const StyledFlashcardContentBox = styled(
  forwardRef((props, ref) => <Box ref={ref} {...props} />),
)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: theme.spacing(0.5, 1, 0.5, 0.5),
  borderRadius: theme.spacing(0.5), // 4px - matches Material-UI Card default borderRadius
  '&:focus-visible': {
    outline: 'none', // Remove default outline
    boxShadow: `inset 0 0 0 2px ${shelfBlue800}`, // Use inset box-shadow, shifted 2px inward
  },
}));

const useStyles = makeStyles((theme) => ({
  threeButtonsInARow: {
    listStyleType: 'none',
    display: 'inline-flex',
    paddingInlineStart: '0px',
  },
  hidden: {
    display: 'none',
  },
}));

const Flashcard = ({ deleteOnClick, editOnClick, flashcard, ...props }) => {
  const ariaLiveMessage = useRef();
  const timerRef = useRef();
  const contentRef = useRef(null);
  const [flip, setFlip] = useState(false);
  const confidenceLevel = flashcard?.confidenceLevel;
  const classes = useStyles();
  const confidenceList = [
    {
      color: 'red',
      level: 'low',
      tooltip: 'Low Confidence',
    },
    {
      color: 'gold',
      level: 'medium',
      tooltip: 'Medium Confidence',
    },
    {
      color: 'green',
      level: 'high',
      tooltip: 'High Confidence',
    },
  ];

  const flashcardText =
    flip ^ flashcard.isSwapped ? flashcard.snippet : flashcard.userText;

  // Move focus to card content when card is flipped
  const handleFlip = () => {
    setFlip(!flip);
    if (contentRef.current) {
      // Use setTimeout to ensure the DOM has updated after the flip
      setTimeout(() => {
        contentRef.current?.focus();
      }, 0);
    }
  };

  return (
    <StyledCard id={flashcard.id} confidencelevel={confidenceLevel} {...props}>
      <AriaLiveRegion
        id={`flipped-flashcard-${flashcard.id}`}
        message={ariaLiveMessage.current}
      />
      <StyledFlashcardContentBox
        ref={contentRef}
        id={`flashcard-content-${flashcard.id}`}
        role="region"
        aria-label="flashcard content"
        tabIndex={-1}
      >
        <StyledCardContent className={'noTranslate'}>
          {flashcardText}
        </StyledCardContent>
      </StyledFlashcardContentBox>
      <StyledBox
        sx={{
          justifyContent: 'flex-end',
          padding: (theme) => theme.spacing(0, 0.5, 0.5, 0),
        }}
      >
        <StyledBox sx={{ alignItems: 'center' }}>
          {flip ? (
            <ul className={classes.threeButtonsInARow}>
              {confidenceList.map((confidence) => (
                <li key={confidence.level}>
                  <IconTooltip
                    id={confidence.level}
                    title={confidence.tooltip}
                    placement="bottom"
                  >
                    <ColorButton
                      aria-label={confidence.tooltip}
                      color={confidence.color}
                      isChecked={confidenceLevel === confidence.level}
                      onClick={() =>
                        flashcard
                          .change({ confidenceLevel: confidence.level })
                          .save()
                      }
                    />
                  </IconTooltip>
                </li>
              ))}
            </ul>
          ) : null}
        </StyledBox>
        <StyledBox>
          <div
            className={classes.hidden}
            id={`flip-card-label-${flashcard.id}`}
          >
            Flip Flashcard. User text:{' '}
            <span className="noTranslate">{flashcard.userText}</span>
          </div>
          <IconTooltip id="flip-card" title="Flip Flashcard">
            <IconButton
              aria-labelledby={`flip-card-label-${flashcard.id}`}
              aria-controls={`flashcard-content-${flashcard.id}`}
              onClick={() => {
                handleFlip();
                ariaLiveMessage.current = `card is flipped ${
                  flip ? 'face-down' : 'face-up'
                }`;
                // when we press the button many times, we need to reset the timeout before setting it.
                // if we reset nothing, then nothing will happen,
                // I see no point in introducing an additional check whether the timeout is set
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                  ariaLiveMessage.current = '';
                }, 5000);
              }}
              disableRipple
            >
              <SwapHorizontalCircle fontSize="small" />
            </IconButton>
          </IconTooltip>
          <OptionsMenu
            cardId={`flashcard-${flashcard.id}`}
            menuItemsList={[
              {
                displayText: 'Edit Flashcard',
                onClick: () => editOnClick(flashcard),
                key: 'edit-flashcard',
              },
              {
                displayText: 'Delete Flashcard',
                onClick: () => deleteOnClick(flashcard),
                key: 'delete-flashcard',
              },
            ]}
            userContentItem={flashcard}
          />
        </StyledBox>
      </StyledBox>
    </StyledCard>
  );
};

Flashcard.propTypes = {
  deleteOnClick: PropTypes.func.isRequired,
  editOnClick: PropTypes.func.isRequired,
  flashcard: PropTypes.shape({
    id: PropTypes.string,
    isSwapped: PropTypes.bool,
    snippet: PropTypes.string,
    userText: PropTypes.string,
  }).isRequired,
};

export default Flashcard;
